<script lang="ts" setup>
import { ref, onMounted, reactive } from "vue";
import { Globals } from "@/Globals";
import CommonVCardDialog from "@/components/common/VCardDialog.vue";
import TagesSaldenAntrag from "@/views/SoftTerm/TagesSaldenAntrag.vue";
import TagesSaldenTimer from "@/views/SoftTerm/TagesSaldenTimer.vue";
import TagesSaldenBelege from "@/views/SoftTerm/TagesSaldenBelege.vue";
import TagesSaldenBestaetigungen from "@/views/SoftTerm/TagesSaldenBestaetigungen.vue";
import { getKennungValueDisplay, getKennungTitle } from "@/DataHelper";
import { globalsBezeichnung, checkBegriff } from "@/local";
import { getSiteData } from "@/NavigationHelper";
import AUApi from "@/api";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";
import { goBack } from "@/NavigationHelper";

const emit = defineEmits<{
  back: [];
  calculated: [data?: any];
}>();
let soll = ref<string>("");
let ist = ref<string>("");
let saldo = ref<string>("");
let differenz = ref<string>("");
let pause = ref<string>("");
let verfallBeginn = ref<string>("");
let verfallEnde = ref<string>("");
let showAntrag = ref<boolean>(true);
let showBestaetigung = ref<boolean>(false);
let isVorgesetzter = ref<boolean>(false);
let showBelege = ref<boolean>(false);

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "PZE_Web_TagesSalden",
  emitBack: () => emit("back"),
};
let zeitbereich = pageParams.zeitbereich;
let aic = pageParams.aic;
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  const siteData = getSiteData();
  if (siteData) {
    if (siteData.zeitbereich) {
      zeitbereich = siteData.zeitbereich;
    }
    if (siteData.aic) {
      aic = siteData.aic;
    }
    if (siteData.sperre === "1.00") {
      showAntrag.value = false;
    }
  }
  if (siteData && siteData.isVorgesetzter) {
    isVorgesetzter.value = true;
    if (checkBegriff("Web_VG_ErfassungBelege")) {
      showBestaetigung.value = true;
    }
  } else {
    if (checkBegriff("Web_Bestaetigungen")) {
      showBestaetigung.value = true;
    }
    if (checkBegriff("RK_Web_ALL_sonstigeBelege")) {
      showBelege.value = true;
    }
  }
  await mountedBase(uiData, pageParams);
  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    soll.value = getKennungValueDisplay(row, "Soll", uiData.columns);
    ist.value = getKennungValueDisplay(row, "Ist", uiData.columns);
    pause.value = getKennungValueDisplay(row, "Pause", uiData.columns);
    saldo.value = getKennungValueDisplay(row, "Saldo", uiData.columns);
    differenz.value = getKennungValueDisplay(row, "Differenz", uiData.columns);
    verfallBeginn.value = getKennungValueDisplay(
      row,
      "VerfallzeitBeginn",
      uiData.columns
    );
    verfallEnde.value = getKennungValueDisplay(
      row,
      "VerfallzeitEnde",
      uiData.columns
    );
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
</script>
<template>
  <v-sheet max-width="100%" v-if="uiData.initialized" class="pb-2">
    <v-card
      :loading="uiData.showLoading"
      height="120px"
      class="ma-2 pa-2"
      variant="text"
    >
        <v-card-text class="pa-0" min-width="450px">
        <v-row>
          <v-col class="dvhCol" cols="6" xs="6" sm="6">
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("Soll", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ soll }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("Ist", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <div>{{ ist }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("Differenz", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ differenz }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("Saldo", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ saldo }}</span>
              </v-col>
            </v-row>
          </v-col>
             <v-col cols="6" xs="6" sm="6">
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("Pause", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ pause }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("VerfallzeitBeginn", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ verfallBeginn }}</span>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{
                  getKennungTitle("VerfallzeitEnde", uiData.columns)
                }}</span>
              </v-col>
              <v-col class="dvhCol">
                <span style="white-space: nowrap">{{ verfallEnde }}</span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <CommonVCardDialog
      textQuery="ZE_Gesamtsaldo_Neu"
      :text="
        globalsBezeichnung(
          Globals.Begriff,
          'Web_Gesamtsaldo',
          'akt. Gesamtsaldo'
        )
      "
      textKennung="BERECHNUNG_MASS"
      :zeitbereich="zeitbereich"
      :aic="aic"
    ></CommonVCardDialog>

    <TagesSaldenAntrag
      v-if="
        checkBegriff('Web_SW_Detailantrag') && !isVorgesetzter && showAntrag
      "
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
    ></TagesSaldenAntrag>

    <TagesSaldenTimer
      v-if="isVorgesetzter"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="px-0 pt-2"
    ></TagesSaldenTimer>

    <TagesSaldenBestaetigungen
      v-if="showBestaetigung"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
    ></TagesSaldenBestaetigungen>

    <TagesSaldenBelege
      v-if="showBelege"
      @back="goBack()"
      :zeitbereichUebergabe="zeitbereich"
      :aic="aic"
      class="pa-0 pt-2"
    ></TagesSaldenBelege>
  </v-sheet>
</template>
<style scoped>
.dvhCard {
  padding-left: 5px;
  height: 100px;
}
.dvhCol {
  padding-bottom: 0px;
  padding-right: 0px;
  padding-left: 10px;
}
@media (max-width: 550px) {
  .v-card__subtitle,
  .v-card__text,
  .v-card__title {
    padding: 10px;
  }
}
</style>